import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { FaSearch } from 'react-icons/fa';
import './AirportSelector.css';

const AirportSelector = ({
  airports = [],
  selectedAirport,
  onAirportSelected,
  disabled = false,
  placeholder = 'Select Airport',
  autoOpen = false,
  onClose = null,
  hideHeader = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAirports, setFilteredAirports] = useState(airports);
  const [anchorRect, setAnchorRect] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredAirports(airports);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = airports.filter(
        (airport) =>
          airport.name.toLowerCase().includes(term) ||
          airport.mainCity.toLowerCase().includes(term) ||
          (airport.state && airport.state.toLowerCase().includes(term))
      );
      setFilteredAirports(filtered);
    }
  }, [searchTerm, airports]);

  const updateAnchorRect = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setAnchorRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateAnchorRect();
      window.addEventListener('scroll', updateAnchorRect, true);
      window.addEventListener('resize', updateAnchorRect);
    }
    return () => {
      window.removeEventListener('scroll', updateAnchorRect, true);
      window.removeEventListener('resize', updateAnchorRect);
    };
  }, [isOpen, updateAnchorRect]);

  // Auto-open when mounted in edit mode
  useEffect(() => {
    if (autoOpen) {
      updateAnchorRect();
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [autoOpen, updateAnchorRect]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInWrapper =
        wrapperRef.current && wrapperRef.current.contains(event.target);
      const clickedInPortal = !!event.target.closest('[data-airport-portal="true"]');
      if (!clickedInWrapper && !clickedInPortal) {
        setIsOpen(false);
        if (onClose) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const selectedAirportData = airports.find((a) => a.code === selectedAirport);

  const handleAirportSelect = (airport) => {
    onAirportSelected(airport);
    setIsOpen(false);
    setSearchTerm('');
    if (onClose) onClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onAirportSelected(null);
    setSearchTerm('');
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      updateAnchorRect();
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setIsOpen(false);
      if (onClose) onClose();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="airport-selector relative w-full h-full"
      onClick={!isOpen ? handleToggle : undefined}
      style={{ cursor: isOpen ? 'default' : 'pointer' }}
    >
      {isOpen ? (
        /* ── EDIT MODE: LocationInput-style inline panel ── */
        <div className="w-full h-full flex flex-col" style={{ overflow: 'visible' }}>
          {!hideHeader && (
            <div
              style={{
                background: '#e8f0fb',
                padding: '10px 16px 8px',
                borderBottom: '1px solid #dce8f8',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '1.5px',
                  color: '#e05a00',
                  textTransform: 'uppercase',
                }}
              >
                AIRPORT
              </span>
            </div>
          )}

          {/* Search row — mirrors .location-input-searchRow */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px 10px',
              gap: '10px',
              background: '#fff',
              flex: 1,
            }}
          >
            <FaSearch style={{ color: '#9facbc', fontSize: '15px', flexShrink: 0 }} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search by airport name or city…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                updateAnchorRect();
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '16px',
                fontWeight: 500,
                color: '#2c3e50',
                background: 'transparent',
                minWidth: 0,
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSearchTerm('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9facbc',
                  fontSize: '13px',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>
      ) : (
        /* ── DISPLAY MODE: same style as FROM/TO ── */
        <div
          className={`cursor-pointer flex items-center justify-between w-full overflow-hidden h-full ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {selectedAirportData ? (
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="font-bold text-gray-900 truncate text-2xl sm:text-3xl md:text-[34px] leading-tight">
                {selectedAirportData.mainCity?.split(',')[0] || selectedAirportData.name.split(',')[0]}
              </div>
              <div className="text-gray-500 text-sm truncate">
                {selectedAirportData.name}
              </div>
            </div>
          ) : (
            <div className="font-bold text-gray-500 truncate text-2xl sm:text-3xl md:text-[34px] leading-tight">
              {placeholder}
            </div>
          )}
          {selectedAirportData && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors ml-2 flex-shrink-0"
              title="Clear selection"
            >
              <FaTimes className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      )}

      {/* Portal results list — shown only when open */}
      {isOpen &&
        !disabled &&
        anchorRect &&
        createPortal(
          <div
            data-airport-portal="true"
            style={{
              position: 'absolute',
              top: anchorRect.bottom + window.scrollY + 2,
              left: anchorRect.left + window.scrollX,
              width: Math.max(anchorRect.width, 340),
              zIndex: 40,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderTop: '2px solid #e05a00',
                boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                borderRadius: '0 0 10px 10px',
                overflow: 'hidden',
              }}
            >
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {filteredAirports.length > 0 ? (
                  <>
                    <div
                      style={{
                        fontSize: '10px',
                        fontWeight: 800,
                        letterSpacing: '1.2px',
                        color: '#9facbc',
                        padding: '10px 16px 6px',
                        textTransform: 'uppercase',
                        background: '#fafbfc',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      AIRPORTS
                    </div>
                    {filteredAirports.map((airport) => (
                      <div
                        key={airport.code}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleAirportSelect(airport);
                        }}
                        className="location-input-cityItem"
                      >
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>
                          {airport.name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                          {airport.mainCity}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div
                    style={{ padding: '24px 16px', textAlign: 'center', color: '#9facbc' }}
                  >
                    No airports found
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AirportSelector;
